import { BarChartData, CategoryValue } from '../types';
import {
  CategoryType,
  GetScoreForContentMetricByCategoryTypeResponse,
  ProductCountByVoiceOrToneRecord
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { METRICTYPE } from 'src/utils/entityDefinitions';

export class CategoryScoreAdapter implements ChartDataAdapter<BarChartData> {
  categoryType: CategoryType;

  data: Array<GetScoreForContentMetricByCategoryTypeResponse['records']>;

  displayNames: {
    category: Record<number, string>;
    subcategory: Record<number, string>;
    brand: Record<number, string>;
  };

  constructor(
    categoryType: CategoryType,
    data: Array<GetScoreForContentMetricByCategoryTypeResponse['records']>,
    displayNames: {
      category: Record<number, string>;
      subcategory: Record<number, string>;
      brand: Record<number, string>;
    }
  ) {
    this.data = data;
    this.categoryType = categoryType;
    this.displayNames = displayNames;
  }

  transform(): BarChartData {
    const getCategoryName = (id: number) => {
      switch (this.categoryType) {
        case CategoryType.Category:
          return this.displayNames.category[id] || id.toString();
        case CategoryType.Subcategory:
          return this.displayNames.subcategory[id] || id.toString();
        case CategoryType.Brand:
          return this.displayNames.brand[id] || id.toString();
        default:
          return id.toString();
      }
    };

    const allCategoryIdsSet = new Set<number>();
    this.data.forEach((records) => {
      records.forEach((record) => {
        allCategoryIdsSet.add(record.id);
      });
    });

    const currentPeriodScores = {};
    if (this.data.length > 0) {
      const currentPeriodData = this.data[0];
      currentPeriodData.forEach((record) => {
        currentPeriodScores[record.id] = record.score;
      });
    }

    const allCategoryIds = Array.from(allCategoryIdsSet);

    const categories = allCategoryIds.map((id) => ({
      id,
      name: getCategoryName(id),
      type: this.categoryType.toLowerCase()
    }));

    const categoryMap = categories.reduce((acc, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    }, {});

    const createDataArray = (
      categoryScores: GetScoreForContentMetricByCategoryTypeResponse['records']
    ): CategoryValue[] => {
      const scoreMap = categoryScores.reduce((acc, curr) => {
        acc[curr.id] = curr.score;
        return acc;
      }, {});

      return allCategoryIds.map((id) => [categoryMap[id], scoreMap[id] !== undefined ? scoreMap[id] : 0]);
    };

    // FIXME: Data is reversed here to match the order of the time periods in the chart legend, but this is a QUICK temporary fix
    // Investigate why the data is reversed in the first place and fix the root cause
    const result = this.data.map((records) => {
      return {
        categories,
        data: createDataArray(records),
        metricType: METRICTYPE.PERCENT,
        type: 'column'
      };
    });

    return { chartData: result, categoryType: this.categoryType };
  }
}

export enum ToneType {
  Friendly = 'Friendly',
  Luxurious = 'Luxurious',
  Humorous = 'Humorous',
  Encouraging = 'Encouraging',
  Enthusiastic = 'Enthusiastic',
  Confident = 'Confident'
}

export enum VoiceType {
  Informative = 'Informative',
  Persuasive = 'Persuasive',
  Sustainable = 'Sustainable',
  Healthy = 'Healthy',
  Playful = 'Playful',
  Supportive = 'Supportive'
}

export class VoiceAndToneProductCountAdapter implements ChartDataAdapter<BarChartData> {
  toneTypes = [
    ToneType.Friendly,
    ToneType.Luxurious,
    ToneType.Humorous,
    ToneType.Encouraging,
    ToneType.Enthusiastic,
    ToneType.Confident
  ];

  voiceTypes = [
    VoiceType.Informative,
    VoiceType.Persuasive,
    VoiceType.Sustainable,
    VoiceType.Healthy,
    VoiceType.Playful,
    VoiceType.Supportive
  ];

  type: 'voice' | 'tone';

  data: [ProductCountByVoiceOrToneRecord[], ProductCountByVoiceOrToneRecord[]];

  constructor(type: 'voice' | 'tone', data: [ProductCountByVoiceOrToneRecord[], ProductCountByVoiceOrToneRecord[]]) {
    this.type = type;
    this.data = data;
  }

  transform(): BarChartData {
    const [mainData, comparisonData] = this.data;

    const categories = (this.type === 'voice' ? this.voiceTypes : this.toneTypes).map(
      (type: ToneType | VoiceType, index: number) => ({
        id: index,
        name: type.toString()
      })
    );

    const getDataArrayWithOrdering = (data: ProductCountByVoiceOrToneRecord[]) => {
      return categories.map((category) => {
        const record = data.find((r) => r.type.toString() === category.name);
        return [category.name, record?.count || 0];
      });
    };
    const mainDataTransformed = getDataArrayWithOrdering(mainData);
    const comparisonDataTransformed = getDataArrayWithOrdering(comparisonData);

    return {
      chartData: [
        {
          categories,
          data: comparisonDataTransformed as CategoryValue[],
          metricType: METRICTYPE.INTEGER,
          type: 'column'
        },
        {
          categories,
          data: mainDataTransformed as CategoryValue[],
          metricType: METRICTYPE.INTEGER,
          type: 'column'
        }
      ]
    };
  }
}
